/**
 * Network Connectivity Probe Utility
 *
 * Provides a resilient check for true network connectivity:
 *
 * 1. Short-circuits immediately if `navigator.onLine` is false (fast path, no network traffic).
 * 2. Deduplicates concurrent in-flight probes so multiple guards/services don't spam `/health`.
 * 3. Caches recent probe results for 3 seconds to avoid hammering the endpoint on rapid state changes.
 * 4. Automatically clears cache on `window` 'online' and 'offline' events.
 */

let inFlightProbe: Promise<boolean> | null = null;
let lastProbeResult: { isOnline: boolean; timestamp: number } | null = null;
const CACHE_TTL_MS = 3000;

export function clearNetworkProbeCache(): void {
  lastProbeResult = null;
  inFlightProbe = null;
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', clearNetworkProbeCache);
  window.addEventListener('offline', clearNetworkProbeCache);
}

export async function checkNetworkOnline(healthUrl?: string | null, force = false): Promise<boolean> {
  // Fast short-circuit: if browser already knows it is offline, trust it immediately.
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return false;
  }

  // If no URL provided and in non-browser or default environment, fallback to navigator.onLine
  if (!healthUrl) {
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  }

  // Return cached result if within TTL and not forced
  if (!force && lastProbeResult && Date.now() - lastProbeResult.timestamp < CACHE_TTL_MS) {
    return lastProbeResult.isOnline;
  }

  // Deduplicate concurrent probes
  if (inFlightProbe) {
    return inFlightProbe;
  }

  const probeUrl = healthUrl.endsWith('/health') ? healthUrl : `${healthUrl.replace(/\/$/, '')}/health`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 2000);

  inFlightProbe = (async () => {
    try {
      const response = await fetch(probeUrl, {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-store',
      });
      const isOnline = response.ok;
      lastProbeResult = { isOnline, timestamp: Date.now() };
      return isOnline;
    } catch {
      // Aborted (2s timeout), connection refused, or blocked by DevTools offline throttling
      lastProbeResult = { isOnline: false, timestamp: Date.now() };
      return false;
    } finally {
      clearTimeout(timeoutId);
      inFlightProbe = null;
    }
  })();

  return inFlightProbe;
}
